import React, { useState, useEffect, useMemo } from 'react';
import { Form, Button, Spinner, Modal, Row, Col } from 'react-bootstrap';
import axios from 'axios';
import { showToast as toast } from '../toastManager';
import '../ChatApp.css';

// ─── Constants ──────────────────────────────────────────────────────────────

const BUILT_IN_PROMPT =
  'Create a new high-converting landing-page section using the selected assets. ' +
  'Build with a mobile-first experience as majority of traffic is on mobile.';

const LLM_PROVIDERS = {
  openai:    { label: 'OpenAI',           models: [
    { value: 'gpt-4.1',                    label: 'GPT-4.1 (latest)' },
    { value: 'gpt-4o',                     label: 'GPT-4o' },
    { value: 'gpt-4o-mini',                label: 'GPT-4o Mini' },
  ]},
  anthropic: { label: 'Anthropic Claude', models: [
    { value: 'claude-opus-4-7',            label: 'Claude Opus 4.7 (latest)' },
    { value: 'claude-sonnet-4-6',          label: 'Claude Sonnet 4.6' },
    { value: 'claude-haiku-4-5-20251001',  label: 'Claude Haiku 4.5' },
  ]},
  gemini:    { label: 'Google Gemini',    models: [
    { value: 'gemini-3.1-pro-preview',     label: 'Gemini 3.1 Pro (latest)' },
    { value: 'gemini-2.5-pro',             label: 'Gemini 2.5 Pro' },
    { value: 'gemini-1.5-pro',             label: 'Gemini 1.5 Pro' },
  ]},
};

const FILTER_OPTIONS = [
  { value: 'All',     label: 'All' },
  { value: 'Groups',  label: 'Groups' },
  { value: 'Context', label: 'Context' },
  { value: 'Image',   label: 'Images' },
  { value: 'Video',   label: 'Videos' },
  { value: 'PDF',     label: 'PDFs' },
];

// ─── Helpers ────────────────────────────────────────────────────────────────

function getYouTubeThumbnail(url) {
  const m = url?.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|shorts\/))([^&?/]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/mqdefault.jpg` : null;
}

async function pollGenerationJob(app_url, jobId) {
  for (let i = 0; i < 300; i++) {
    let job;
    try {
      const res = await axios.get(`${app_url}/api/ai_generation/job/${jobId}`, { withCredentials: true });
      job = res.data?.job;
    } catch (err) {
      if (i > 2) throw new Error('Lost connection to job status');
      await new Promise(r => setTimeout(r, 2000));
      continue;
    }
    if (!job) throw new Error('Job not found');
    if (job.status === 'succeeded') return job.result || {};
    if (job.status === 'failed') throw new Error(job.error?.message || 'Generation failed');
    await new Promise(r => setTimeout(r, 2000));
  }
  throw new Error('Generation timed out');
}

// ─── Component ──────────────────────────────────────────────────────────────

const NewSections = ({ ViewedPage, shop_domain, app_url }) => {
  useEffect(() => { if (ViewedPage) ViewedPage(59); }, []);

  // Loaders
  const [loading, setLoading] = useState(true);

  // Data
  const [landingPages, setLandingPages] = useState([]);
  const [assets, setAssets] = useState([]);
  const [groups, setGroups] = useState([]);
  const [storeCtx, setStoreCtx] = useState({ businessContext: null, allTextContent: {} });

  // Picker selection
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('All');
  const [selectedLp, setSelectedLp] = useState('');
  const [selectedGroupIds, setSelectedGroupIds] = useState(new Set());
  const [selectedAssetIds, setSelectedAssetIds] = useState(new Set());
  const [includeBusinessContext, setIncludeBusinessContext] = useState(false);
  const [selectedTextPages, setSelectedTextPages] = useState(new Set());
  const [llmProvider, setLlmProvider] = useState('openai');
  const [llmModel, setLlmModel] = useState('gpt-4.1');

  // Generation
  const [generating, setGenerating] = useState(false);
  const [generatedSections, setGeneratedSections] = useState([]);

  // ─── Load on mount ────────────────────────────────────────────────────────

  useEffect(() => {
    if (!shop_domain || !app_url) return;
    (async () => {
      try {
        const [lpsRes, assetsRes, groupsRes, ctxRes] = await Promise.all([
          axios.post(`${app_url}/api/firebase/get_landing_pages`, {}, { withCredentials: true }),
          axios.post(`${app_url}/api/assets/get_all`, { shop_domain }, { withCredentials: true }),
          axios.get(`${app_url}/api/store/asset-groups`, { withCredentials: true }),
          axios.get(`${app_url}/api/store/ai-context`, { withCredentials: true }),
        ]);
        if (lpsRes.data?.success) setLandingPages(lpsRes.data.landing_pages || []);
        if (assetsRes.data?.success) setAssets(assetsRes.data.data || []);
        if (groupsRes.data?.success) setGroups(groupsRes.data.data || []);
        if (ctxRes.data?.success) setStoreCtx({
          businessContext: ctxRes.data.data?.businessContext || null,
          allTextContent: ctxRes.data.data?.allTextContent || {},
        });
      } catch (err) {
        console.error('NewSections load failed', err);
        toast.error('Failed to load New Sections data');
      } finally {
        setLoading(false);
      }
    })();
  }, [shop_domain, app_url]);

  // ─── Derived state ────────────────────────────────────────────────────────

  const assetById = useMemo(() => {
    const m = new Map();
    for (const a of assets) m.set(String(a._id), a);
    return m;
  }, [assets]);

  const imageAssets   = useMemo(() => assets.filter(a => a.type === 'Image'),  [assets]);
  const videoAssets   = useMemo(() => assets.filter(a => a.type === 'Video' || a.type === 'Video Link'), [assets]);
  const pdfAssets     = useMemo(() => assets.filter(a => a.type === 'PDF'),    [assets]);

  const textPagesAvailable = useMemo(
    () => Object.keys(storeCtx.allTextContent || {}).filter(p => {
      const v = storeCtx.allTextContent[p];
      return v && (typeof v === 'string' ? v.trim() : true);
    }),
    [storeCtx.allTextContent]
  );

  const totalSelected =
    selectedGroupIds.size +
    selectedAssetIds.size +
    (includeBusinessContext ? 1 : 0) +
    selectedTextPages.size;

  // ─── Picker toggles ───────────────────────────────────────────────────────

  const toggleGroup = (id) => setSelectedGroupIds(prev => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    return next;
  });
  const toggleAsset = (id) => setSelectedAssetIds(prev => {
    const next = new Set(prev);
    next.has(String(id)) ? next.delete(String(id)) : next.add(String(id));
    return next;
  });
  const toggleTextPage = (page) => setSelectedTextPages(prev => {
    const next = new Set(prev);
    next.has(page) ? next.delete(page) : next.add(page);
    return next;
  });

  const handleProviderChange = (e) => {
    const p = e.target.value;
    setLlmProvider(p);
    setLlmModel(LLM_PROVIDERS[p].models[0].value);
  };

  const filePreviewUrl = (a) =>
    a.publicUrl || a.url ||
    (a.fileName ? `${app_url}/api/assets/file/${a._id}?shop_domain=${shop_domain}` : null);

  // ─── Generate ─────────────────────────────────────────────────────────────

  const handleGenerate = async () => {
    if (!selectedLp) { toast.error('Pick a landing page first'); return; }
    if (totalSelected === 0) { toast.error('Pick at least one asset, group, or context'); return; }

    // Expand groups into the asset / context / text-page sets.
    const baseAssetIds = new Set([...selectedAssetIds]);
    let aggregateBc = includeBusinessContext;
    const aggregateTextPages = new Set([...selectedTextPages]);

    for (const g of groups) {
      if (!selectedGroupIds.has(g.group_id)) continue;
      for (const id of (g.asset_ids || [])) baseAssetIds.add(String(id));
      if (g.include_business_context) aggregateBc = true;
      for (const p of (g.text_content_pages || [])) aggregateTextPages.add(p);
    }

    const resolved = [...baseAssetIds].map(id => assetById.get(id)).filter(Boolean);
    const images  = resolved.filter(a => a.type === 'Image');
    const videos  = resolved.filter(a => a.type === 'Video' || a.type === 'Video Link');
    const pdfs    = resolved.filter(a => a.type === 'PDF');

    const textContent = aggregateTextPages.has(selectedLp)
      ? (storeCtx.allTextContent?.[selectedLp] || null)
      : null;

    setGenerating(true);
    try {
      const startRes = await axios.post(
        `${app_url}/api/ai_generation/generate_sections`,
        {
          page: selectedLp,
          outputType: 'sections',
          mode: 'create',
          selectedAssets: {
            businessContext: aggregateBc ? (storeCtx.businessContext || null) : null,
            textContent,
            forms: [],
            images: images.map(a => ({
              title: a.title,
              description: a.description || null,
              url: a.url || (a.fileName ? filePreviewUrl(a) : null),
            })),
            videos: videos.map(a => ({ title: a.title, url: a.url })),
            pdfs: pdfs.map(a => ({ title: a.title })),
            sections: [],
          },
          selectedSection: null,
          jumpLinks: [],
          chatContext: { lastExchange: null, summary: '' },
          llmProvider,
          llmModel,
          sessionCacheId: null,
          userMessage: BUILT_IN_PROMPT,
        },
        { withCredentials: true }
      );
      if (!startRes.data?.success || !startRes.data?.jobId) {
        throw new Error(startRes.data?.message || 'Server did not return a jobId');
      }
      const result = await pollGenerationJob(app_url, startRes.data.jobId);
      const sections = Array.isArray(result?.sections) ? result.sections : [];
      if (sections.length === 0) {
        toast.info('No section returned. Try a different combination.');
      } else {
        setGeneratedSections(prev => [...prev, ...sections]);
        toast.success(`Generated ${sections.length} section${sections.length > 1 ? 's' : ''}`);
      }
    } catch (err) {
      console.error('NewSections handleGenerate error', err);
      toast.error(err.message || 'Generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleClear = () => {
    if (generatedSections.length === 0) return;
    setGeneratedSections([]);
  };

  // ─── Picker render helpers ────────────────────────────────────────────────

  const showGroups  = filter === 'All' || filter === 'Groups';
  const showContext = filter === 'All' || filter === 'Context';
  const showImages  = filter === 'All' || filter === 'Image';
  const showVideos  = filter === 'All' || filter === 'Video';
  const showPdfs    = filter === 'All' || filter === 'PDF';

  const summarizeGroup = (g) => {
    const parts = [];
    const n = (g.asset_ids || []).length;
    if (n) parts.push(`${n} asset${n > 1 ? 's' : ''}`);
    if (g.include_business_context) parts.push('Business Context');
    const tp = (g.text_content_pages || []).length;
    if (tp) parts.push(`${tp} text page${tp > 1 ? 's' : ''}`);
    return parts.length ? parts.join(' · ') : 'Empty';
  };

  const Card = ({ id, name, subLabel, selected, onToggle, previewSlot, iconFallback }) => (
    <div
      onClick={() => onToggle(id)}
      title={name}
      style={{
        border: selected ? '2px solid #132848' : '1px solid #dee2e6',
        background: selected ? '#eef2f6' : '#fff',
        borderRadius: 8,
        overflow: 'hidden',
        cursor: 'pointer',
        position: 'relative',
        userSelect: 'none',
      }}
    >
      <div style={{ height: 90, background: '#f0f2f5', position: 'relative', overflow: 'hidden' }}>
        {previewSlot
          ? previewSlot
          : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', fontSize: 28 }}>
              {iconFallback || '📋'}
            </div>
          )}
        {selected && (
          <div style={{
            position: 'absolute', top: 4, right: 4,
            background: '#132848', color: '#fff',
            borderRadius: '50%', width: 18, height: 18,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 10, fontWeight: 700,
          }}>✓</div>
        )}
      </div>
      <div style={{ padding: '5px 8px' }}>
        <div style={{ fontSize: 12, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {name}
        </div>
        {subLabel && <div style={{ fontSize: 10, color: '#6c757d', marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{subLabel}</div>}
      </div>
    </div>
  );

  const GroupHeader = ({ label }) => (
    <div style={{ fontSize: 10, fontWeight: 700, color: '#9aa2ad', textTransform: 'uppercase', letterSpacing: 0.5, margin: '12px 0 6px' }}>
      {label}
    </div>
  );

  const gridStyle = { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 10 };

  // ─── Render ───────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className='main-container' id='new-sections-main-container'>
        <div className='main_conatiner_spinner'><Spinner animation='border' /></div>
      </div>
    );
  }

  return (
    <div className='main-container' id='new-sections-main-container'>
      <div
        className='main-title mt-4 mx-4 d-flex justify-content-between align-items-center'
        style={{
          marginBottom: '10px',
          border: '1px solid #e9ecef',
          borderRadius: '8px',
          padding: '16px 20px',
          backgroundColor: '#ffffff',
        }}
      >
        <div className='d-flex align-items-center gap-2'>
          <h2 style={{ margin: 0, fontWeight: 'bold' }}>New Sections</h2>
          {generatedSections.length > 0 && (
            <span className='campaign-tag-new'>{generatedSections.length}</span>
          )}
          {generatedSections.length > 0 && (
            <button
              type='button'
              onClick={handleClear}
              className='btn btn-link btn-sm p-0 ms-1'
              style={{ fontSize: 12, color: '#6c757d', textDecoration: 'underline' }}
              disabled={generating}
            >
              Clear
            </button>
          )}
        </div>
        <Button
          className='sxi_primary_blue_btn'
          onClick={() => setPickerOpen(true)}
          disabled={generating}
          style={{ padding: '8px 16px' }}
        >
          {generating
            ? <><Spinner size='sm' animation='border' /> Generating…</>
            : 'Select & Generate'}
        </Button>
      </div>

      {generatedSections.length === 0 && !generating && (
        <div
          className='mx-4 p-4 text-center'
          style={{ border: '1px solid #e9ecef', borderRadius: 8, backgroundColor: '#ffffff', color: '#6B7280' }}
        >
          No sections yet. Click <strong style={{ color: '#132848' }}>Select &amp; Generate</strong> to create your first one.
        </div>
      )}

      {generatedSections.length > 0 && (
        <div className='mx-3 p-2'>
          {generatedSections.map((section, idx) => (
            <div
              key={`${section.section_id || 'sec'}-${idx}`}
              className='sxi-existing-div-wrapper'
              style={{
                marginBottom: '1rem',
                border: '1px solid #e9ecef',
                borderRadius: '8px',
                padding: '10px',
                backgroundColor: '#ffffff',
              }}
            >
              <div style={{
                padding: '8px 14px',
                background: '#f8f9fa',
                borderBottom: '1px solid #e9ecef',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 13,
              }}>
                <strong style={{ color: 'rgb(5,44,101)' }}>
                  {section.section_name || `Section ${idx + 1}`}
                </strong>
                {section.section_type && (
                  <span className='campaign-tag-new ms-1'>{section.section_type}</span>
                )}
                {section.firebaseUrl && (
                  <a
                    href={section.firebaseUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='btn btn-sm btn-outline-secondary ms-auto'
                    style={{ fontSize: 12 }}
                  >
                    Open URL
                  </a>
                )}
              </div>
              <iframe
                srcDoc={
                  `<!DOCTYPE html><html><head>` +
                  `<meta charset="utf-8">` +
                  `<meta name="viewport" content="width=device-width,initial-scale=1">` +
                  `<style>${section.css || ''}</style>` +
                  `</head><body style="margin:0;padding:0">${section.html || ''}` +
                  (section.js ? `<script>${section.js}<\/script>` : '') +
                  `</body></html>`
                }
                style={{ width: '100%', height: 520, border: 'none', display: 'block', background: '#ffffff' }}
                sandbox='allow-scripts allow-same-origin'
                title={section.section_name || `section-${idx}`}
              />
            </div>
          ))}
        </div>
      )}

      {/* Picker Modal */}
      <Modal
        show={pickerOpen}
        onHide={() => !generating && setPickerOpen(false)}
        size='lg'
        centered
        scrollable
      >
        <Modal.Header closeButton={!generating}>
          <Modal.Title>Pick a Landing Page &amp; Assets</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          <Form.Group className='mb-3' controlId='new-sections-lp-select'>
            <Form.Label className='mb-1'>Landing Page</Form.Label>
            <Form.Select
              className='form-select form-select-sm'
              value={selectedLp}
              onChange={(e) => setSelectedLp(e.target.value)}
              disabled={generating}
              style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '6px 12px', fontSize: 14 }}
            >
              <option value=''>Select a landing page…</option>
              {landingPages.map(lp => <option key={lp} value={lp}>{lp}</option>)}
            </Form.Select>
          </Form.Group>

          <Row className='mb-3'>
            <Col md={6}>
              <Form.Label className='mb-1'>AI Provider</Form.Label>
              <Form.Select
                className='form-select form-select-sm'
                value={llmProvider}
                onChange={handleProviderChange}
                disabled={generating}
              >
                {Object.entries(LLM_PROVIDERS).map(([k, v]) => (
                  <option key={k} value={k}>{v.label}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={6}>
              <Form.Label className='mb-1'>Model</Form.Label>
              <Form.Select
                className='form-select form-select-sm'
                value={llmModel}
                onChange={(e) => setLlmModel(e.target.value)}
                disabled={generating}
              >
                {LLM_PROVIDERS[llmProvider].models.map(m => (
                  <option key={m.value} value={m.value}>{m.label}</option>
                ))}
              </Form.Select>
            </Col>
          </Row>

          {/* Filter pills */}
          <div className='d-flex justify-content-between align-items-center mb-2'>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {FILTER_OPTIONS.map(o => (
                <button
                  key={o.value}
                  type='button'
                  onClick={() => setFilter(o.value)}
                  style={{
                    padding: '4px 12px', borderRadius: 20,
                    border: filter === o.value ? '2px solid #132848' : '1px solid #dee2e6',
                    background: filter === o.value ? '#eef2f6' : '#fff',
                    color: filter === o.value ? '#132848' : '#495057',
                    fontSize: 12, cursor: 'pointer',
                    fontWeight: filter === o.value ? 600 : 400,
                  }}
                >
                  {o.label}
                </button>
              ))}
            </div>
            {totalSelected > 0 && (
              <span className='campaign-tag-new'>{totalSelected} selected</span>
            )}
          </div>

          <div style={{ maxHeight: '45vh', overflowY: 'auto', paddingRight: 4 }}>
            {showGroups && groups.length > 0 && (
              <>
                <GroupHeader label='Asset Groups' />
                <div style={gridStyle}>
                  {groups.map(g => (
                    <Card
                      key={g.group_id}
                      id={g.group_id}
                      name={g.name}
                      subLabel={summarizeGroup(g)}
                      selected={selectedGroupIds.has(g.group_id)}
                      onToggle={toggleGroup}
                      iconFallback='🧩'
                    />
                  ))}
                </div>
              </>
            )}

            {showContext && (storeCtx.businessContext || textPagesAvailable.length > 0) && (
              <>
                <GroupHeader label='Context' />
                <div style={gridStyle}>
                  {storeCtx.businessContext && (
                    <Card
                      id='__bc__'
                      name='Business Context'
                      selected={includeBusinessContext}
                      onToggle={() => setIncludeBusinessContext(p => !p)}
                      iconFallback='🏢'
                    />
                  )}
                  {textPagesAvailable.map(page => (
                    <Card
                      key={`tp-${page}`}
                      id={`tp-${page}`}
                      name='Text Content'
                      subLabel={page.replace(/^https?:\/\//, '').slice(0, 28)}
                      selected={selectedTextPages.has(page)}
                      onToggle={() => toggleTextPage(page)}
                      iconFallback='📃'
                    />
                  ))}
                </div>
              </>
            )}

            {showImages && imageAssets.length > 0 && (
              <>
                <GroupHeader label='Images' />
                <div style={gridStyle}>
                  {imageAssets.map(a => {
                    const previewUrl = a.url || (a.fileName ? filePreviewUrl(a) : null);
                    return (
                      <Card
                        key={a._id}
                        id={a._id}
                        name={a.title}
                        selected={selectedAssetIds.has(String(a._id))}
                        onToggle={toggleAsset}
                        iconFallback='🖼️'
                        previewSlot={previewUrl ? (
                          <img src={previewUrl} alt={a.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : null}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {showVideos && videoAssets.length > 0 && (
              <>
                <GroupHeader label='Videos' />
                <div style={gridStyle}>
                  {videoAssets.map(a => {
                    const yt = getYouTubeThumbnail(a.url || a.videoUrl || a.fileUrl);
                    return (
                      <Card
                        key={a._id}
                        id={a._id}
                        name={a.title}
                        selected={selectedAssetIds.has(String(a._id))}
                        onToggle={toggleAsset}
                        iconFallback='🎬'
                        previewSlot={yt ? (
                          <img src={yt} alt={a.title} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                        ) : null}
                      />
                    );
                  })}
                </div>
              </>
            )}

            {showPdfs && pdfAssets.length > 0 && (
              <>
                <GroupHeader label='PDFs' />
                <div style={gridStyle}>
                  {pdfAssets.map(a => (
                    <Card
                      key={a._id}
                      id={a._id}
                      name={a.title}
                      selected={selectedAssetIds.has(String(a._id))}
                      onToggle={toggleAsset}
                      iconFallback='📄'
                    />
                  ))}
                </div>
              </>
            )}

            {/* Empty state inside picker */}
            {!groups.length && !imageAssets.length && !videoAssets.length && !pdfAssets.length &&
              !storeCtx.businessContext && textPagesAvailable.length === 0 && (
              <div className='text-muted small text-center py-3'>
                No assets, groups, or context available yet. Add them in Settings → Assets.
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            className='sxi_primary_blue_btn'
            onClick={() => { setPickerOpen(false); handleGenerate(); }}
            disabled={!selectedLp || totalSelected === 0 || generating}
            style={{ padding: '8px 16px' }}
          >
            {generating
              ? <><Spinner size='sm' animation='border' /> Generating…</>
              : `Generate${totalSelected ? ` (${totalSelected})` : ''}`}
          </Button>
          <button className='back-button' onClick={() => setPickerOpen(false)} disabled={generating}>Cancel</button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default NewSections;
